import React, { useState, useEffect } from 'react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { AppDrawer } from './ui/AppDrawer';
import { JobAllocationPicker, jobLabel } from './JobAllocationPicker';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Package, Warehouse, Truck, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { purchaseOrdersAPI, invalidateCache } from '../lib/api';
import { PODocumentsPanel } from './po/PODocumentsPanel';
import { formatQty } from '../lib/qtyUtils';

/**
 * POAssignmentModal - Drawer for assigning received PO inventory
 * PHASE 3: Triggered when a PO is marked as "Fully Received"
 * 
 * Features:
 * - Shows pending (unassigned) items only
 * - Handles "nothing to assign" state gracefully
 * - Confirmation dialog when closing with pending items
 * - Supports both warehouse and vehicle destinations
 * - Handles multi-line POs with bulk assignment
 */

export const POAssignmentModal = ({ 
  open, 
  poId,
  poNumber,
  mode = 'assign',
  onSuccess,
  onOpenChange 
}) => {
  const isReceive = mode === 'receive';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [assignmentData, setAssignmentData] = useState(null);
  const [destinationType, setDestinationType] = useState('warehouse');
  const [destinationId, setDestinationId] = useState('');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [autoCreatedParts, setAutoCreatedParts] = useState(0);
  const [autoCreatedPartNames, setAutoCreatedPartNames] = useState([]);
  // Phase 3B: per-line editable assignment qty (defaults to each line's
  // quantity_pending so the common "assign everything available" flow keeps
  // a single-click experience for the user).
  const [lineQuantities, setLineQuantities] = useState({});
  const [job, setJob] = useState(null);
  const [jobPrefilled, setJobPrefilled] = useState(false);

  useEffect(() => {
    if (open && poId) loadAssignmentDefaults();
  }, [open, poId, mode]);

  useEffect(() => {
    if (!open) {
      setLoading(true);
      setSaving(false);
      setError(null);
      setSuccess(false);
      setAssignmentData(null);
      setDestinationType('warehouse');
      setDestinationId('');
      setShowCloseConfirm(false);
      setAutoCreatedParts(0);
      setAutoCreatedPartNames([]);
      setLineQuantities({});
      setJob(null);
      setJobPrefilled(false);
    }
  }, [open]);

  const loadAssignmentDefaults = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await purchaseOrdersAPI.getAssignmentDefaults(poId, mode);
      const data = response.data;
      setAssignmentData(data);
      // Phase 3B: seed editable qty from server-computed pending per line.
      const seeded = {};
      (data.line_items || []).forEach(li => {
        seeded[li.id] = Number(li.quantity_pending ?? li.quantity_received ?? 0);
      });
      setLineQuantities(seeded);
      setJob(data.po_job ? { id: data.po_job.id, job_number: data.po_job.job_number, job_name: data.po_job.job_name } : null);
      setJobPrefilled(!!data.po_job);
      if (data.default_warehouse_id && data.warehouses?.some(w => w.id === data.default_warehouse_id)) {
        setDestinationType('warehouse');
        setDestinationId(data.default_warehouse_id);
      } else if (data.warehouses?.length > 0) {
        setDestinationType('warehouse');
        setDestinationId(data.warehouses[0].id);
      } else if (data.vehicles?.length > 0) {
        setDestinationType('vehicle');
        setDestinationId(data.vehicles[0].id);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to load assignment data';
      setError(typeof errorMsg === 'string' ? errorMsg : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!destinationId) { toast.error('Please select a destination'); return; }
    if (!assignmentData?.line_items?.length) { toast.error('No items to assign'); return; }

    // Phase 3B: build assignments from the editable per-line quantities and
    // skip lines the user explicitly cleared (qty=0). Client-side guard is
    // belt-and-braces — the server enforces the same per-line capacity.
    const assignments = [];
    const receive = [];
    for (const li of assignmentData.line_items) {
      const qty = Number(lineQuantities[li.id] ?? li.quantity_pending ?? li.quantity_received ?? 0);
      const pending = Number(li.quantity_pending ?? li.quantity_received ?? 0);
      if (!Number.isFinite(qty) || qty <= 0) continue;
      if (qty > pending) {
        toast.error(isReceive
          ? `${li.item_name}: can't receive ${qty}, only ${pending} left to receive.`
          : `${li.item_name}: can't assign ${qty}, only ${pending} available.`);
        return;
      }
      if (isReceive) receive.push({ line_item_id: li.id, quantity: qty, expected_received: Number(li.quantity_received ?? 0) });
      assignments.push({
        line_item_id: li.id,
        destination_type: destinationType,
        destination_id: destinationId,
        quantity: qty,
      });
    }
    if (assignments.length === 0) {
      toast.error(isReceive ? 'Enter at least one quantity to receive.' : 'Enter at least one quantity to assign.');
      return;
    }

    setSaving(true);
    setError(null);
    const allocJob = destinationType === 'warehouse' ? job : null;
    try {
      const response = await purchaseOrdersAPI.assignReceivedInventory(poId, assignments, allocJob?.id, isReceive ? receive : undefined);
      const result = response.data;

      if (result.success) {
        setSuccess(true);
        setAutoCreatedParts(result.auto_created_parts || 0);
        setAutoCreatedPartNames(result.auto_created_part_names || []);
        if (result.assignments.length === 0) {
          toast.info('Inventory was already assigned');
        } else if (isReceive) {
          toast.success(`Received ${formatQty(totalQuantity)} into ${getDestinationName()}${result.job_allocation ? ` and allocated to Job ${jobLabel(result.job_allocation.job)}` : ''}`);
        } else if (result.job_allocation) {
          toast.success(`Received into ${getDestinationName()} and allocated to Job ${jobLabel(result.job_allocation.job)}`);
        } else if (result.auto_created_parts > 0) {
          toast.success(`${result.auto_created_parts} new part(s) added to your catalogue and stocked in ${getDestinationName()}.`);
        } else {
          toast.success(`Inventory assigned successfully to ${getDestinationName()}`);
        }
        setTimeout(() => {
          if (onSuccess) onSuccess(result);
          if (onOpenChange) onOpenChange(false);
        }, 1500);
      } else {
        const failed = result.assignments.filter(a => !a.success);
        const succeeded = result.assignments.filter(a => a.success);
        const failedDetails = failed.map(a => `${a.item_name}: ${a.message || 'unknown error'}`).join(', ');
        toast.warning(`${succeeded.length} assigned, ${failed.length} failed`);
        setError(`Failed items: ${failedDetails}`);
      }
    } catch (err) {
      let errorMsg = 'Failed to assign inventory';
      const detail = err.response?.data?.detail;
      if (detail) {
        if (typeof detail === 'string') errorMsg = detail;
        else if (Array.isArray(detail)) errorMsg = detail.map(d => d.msg || d.message || JSON.stringify(d)).join('; ');
        else if (typeof detail === 'object' && detail.message) errorMsg = detail.message;
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.message) {
        errorMsg = err.message;
      }
      setError(errorMsg);
      toast.error(errorMsg);
      if (isReceive && err.response?.status === 409) {
        invalidateCache('/purchase-orders');
        loadAssignmentDefaults();
      }
    } finally {
      setSaving(false);
    }
  };

  const getDestinationName = () => {
    if (destinationType === 'warehouse') {
      return assignmentData?.warehouses?.find(w => w.id === destinationId)?.name || 'warehouse';
    }
    return assignmentData?.vehicles?.find(v => v.id === destinationId)?.name || 'vehicle';
  };

  const destinations = destinationType === 'warehouse' 
    ? assignmentData?.warehouses || []
    : assignmentData?.vehicles || [];

  const totalQuantity = (assignmentData?.line_items || []).reduce((sum, li) => {
    const q = Number(lineQuantities[li.id] ?? li.quantity_pending ?? li.quantity_received ?? 0);
    return sum + (Number.isFinite(q) ? q : 0);
  }, 0);
  const hasPendingItems = assignmentData?.line_items?.length > 0 && !assignmentData?.already_assigned;
  const alreadyAssigned = assignmentData?.already_assigned === true;
  const nothingToAssign = !loading && !error && (!assignmentData?.line_items?.length || alreadyAssigned);
  // Phase 3B: wording adapts to PO state — don't promise "completion" while
  // ordered units are still outstanding.
  const isPartialPO = (assignmentData?.status === 'Partially Received');
  const drawerSubtitle = isReceive
    ? (nothingToAssign ? ' - Nothing left to receive on this PO.' : ' - Enter what arrived, choose where it goes, and optionally allocate it to a job.')
    : nothingToAssign
    ? ' - All received items have been assigned.'
    : isPartialPO
      ? ' - Assign received inventory to a warehouse or vehicle. More items are still on order.'
      : ' - Assign received inventory to a warehouse or vehicle.';

  const handleCloseAttempt = () => {
    if (isReceive || success || loading || error || nothingToAssign) {
      if (onOpenChange) onOpenChange(false);
      return;
    }
    if (hasPendingItems) {
      setShowCloseConfirm(true);
      return;
    }
    if (onOpenChange) onOpenChange(false);
  };

  const handleForceClose = () => {
    setShowCloseConfirm(false);
    if (onOpenChange) onOpenChange(false);
  };

  const getFooter = () => {
    if (loading) return null;
    if (error && !assignmentData) {
      return (
        <>
          <Button variant="outline" onClick={() => onOpenChange && onOpenChange(false)} data-testid="po-assign-close-btn">Close</Button>
          <Button onClick={loadAssignmentDefaults} data-testid="po-assign-retry-btn">Retry</Button>
        </>
      );
    }
    if (nothingToAssign || success) {
      return <Button variant="outline" onClick={() => onOpenChange && onOpenChange(false)} data-testid="po-assign-close-btn">Close</Button>;
    }
    return (
      <>
        <Button variant="outline" onClick={handleCloseAttempt} data-testid="po-assign-cancel-btn">Cancel</Button>
        <Button onClick={handleAssign} disabled={saving || !destinationId || destinations.length === 0} data-testid="assign-inventory-btn">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{destinationType === 'warehouse' && job ? 'Receiving & allocating...' : isReceive ? 'Receiving...' : 'Assigning...'}</> : <><CheckCircle2 className="w-4 h-4 mr-2" />{destinationType === 'warehouse' && job ? 'Receive & Allocate' : isReceive ? 'Receive & Assign' : 'Assign'}</>}
        </Button>
      </>
    );
  };

  return (
    <>
      <AppDrawer
        open={open}
        onOpenChange={(nextOpen) => { if (!nextOpen) handleCloseAttempt(); }}
        title={
          <span className="flex items-center gap-2">
            <Package className="w-5 h-5 text-primary" />
            {isReceive ? 'Receive & Assign' : 'Assign Received Inventory'}
          </span>
        }
        description={`PO #${poNumber || assignmentData?.po_number || ''}${drawerSubtitle}`}
        size="md"
        isDirty={!isReceive && hasPendingItems && !success}
        data-testid="po-assignment-drawer"
        footer={getFooter()}
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : error && !assignmentData ? (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-800">Error Loading Data</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        ) : nothingToAssign ? (
          <div className="py-8 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-green-800 dark:text-green-400">{isReceive ? 'Nothing to Receive' : 'Nothing to Assign'}</h3>
            <p className="text-sm text-green-700 dark:text-green-500 mt-1" data-testid="po-assign-nothing-msg">
              {isReceive ? 'Every ordered item on this PO has already been received.' : alreadyAssigned ? 'All received items have already been assigned to inventory.' : 'No items are pending assignment for this PO.'}
            </p>
            {assignmentData?.inventory_assigned_at && (
              <p className="text-xs text-muted-foreground mt-2">
                Assigned on {new Date(assignmentData.inventory_assigned_at).toLocaleDateString()}
              </p>
            )}
          </div>
        ) : success ? (
          <div className="py-8 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-green-800 dark:text-green-400">{isReceive ? 'Received' : 'Assignment Complete'}</h3>
            <p className="text-sm text-green-700 dark:text-green-500 mt-1">Inventory has been added to {getDestinationName()}</p>
            {autoCreatedParts > 0 && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 w-full text-left" data-testid="auto-created-parts-notice">
                <p className="font-medium">{autoCreatedParts} new part{autoCreatedParts > 1 ? 's were' : ' was'} added to your catalogue:</p>
                <ul className="mt-1 list-disc list-inside">
                  {autoCreatedPartNames.map((name, i) => <li key={i}>{name}</li>)}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-5">
            {/* Items Summary */}
            <div className="p-4 bg-muted/50 rounded-lg">
              <h4 className="text-sm font-medium mb-3">
                {isReceive ? 'Receiving now' : isPartialPO ? 'Received items available to assign' : 'Items to Assign'}
              </h4>
              <div className="space-y-2">
                {assignmentData?.line_items?.map((li) => {
                  const pending = Number(li.quantity_pending ?? li.quantity_received ?? 0);
                  const received = Number(li.quantity_received ?? 0);
                  const ordered = Number(li.quantity_ordered ?? 0);
                  const currentQty = lineQuantities[li.id] ?? pending;
                  const exceeded = Number(currentQty) > pending;
                  return (
                    <div key={li.id} className="flex items-center gap-2 text-sm py-1.5 border-b border-border/50 last:border-0">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Package className="w-4 h-4 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <div className="truncate">{li.item_name}</div>
                          <div className="text-[11px] text-muted-foreground">
                            {isReceive
                              ? `${formatQty(pending)} left to receive · ${formatQty(received)} of ${formatQty(ordered)} received`
                              : ordered > received
                              ? `${formatQty(pending)} available · ${formatQty(received)} of ${formatQty(ordered)} received`
                              : `${formatQty(pending)} available · ${formatQty(received)} received`}
                          </div>
                        </div>
                        {!li.is_linked_to_parts_list && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded shrink-0">New</span>}
                      </div>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={pending}
                        step={1}
                        value={currentQty}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const v = raw === '' ? '' : Number(raw);
                          setLineQuantities(prev => ({ ...prev, [li.id]: v }));
                        }}
                        className={`w-16 h-8 text-right text-sm rounded-md border px-2 bg-background text-foreground focus:outline-none focus:ring-1 ${exceeded ? 'border-red-400 focus:ring-red-400' : 'border-input focus:ring-primary'}`}
                        data-testid={`assign-qty-${li.id}`}
                        aria-label={`${isReceive ? 'Quantity receiving now' : 'Quantity to assign'} for ${li.item_name}`}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-3 pt-2 border-t border-border text-sm">
                <span className="font-medium">{isReceive ? 'Total receiving now' : 'Total to assign'}</span>
                <span className="font-bold text-primary" data-testid="po-assign-total">{formatQty(totalQuantity)}</span>
              </div>
            </div>

            {/* Destination Type Selector */}
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-wide">Destination Type</Label>
              <div className="flex gap-2">
                <Button type="button" variant={destinationType === 'warehouse' ? 'default' : 'outline'} className="flex-1"
                  onClick={() => {
                    setDestinationType('warehouse');
                    if (assignmentData?.warehouses?.length > 0) setDestinationId(assignmentData.warehouses.some(w => w.id === assignmentData.default_warehouse_id) ? assignmentData.default_warehouse_id : assignmentData.warehouses[0].id);
                    else setDestinationId('');
                  }} data-testid="destination-type-warehouse">
                  <Warehouse className="w-4 h-4 mr-2" />Warehouse
                </Button>
                <Button type="button" variant={destinationType === 'vehicle' ? 'default' : 'outline'} className="flex-1"
                  onClick={() => {
                    setDestinationType('vehicle');
                    if (assignmentData?.vehicles?.length > 0) setDestinationId(assignmentData.vehicles[0].id);
                    else setDestinationId('');
                  }} data-testid="destination-type-vehicle">
                  <Truck className="w-4 h-4 mr-2" />Vehicle
                </Button>
              </div>
            </div>

            {/* Destination Selector */}
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-wide">
                {destinationType === 'warehouse' ? 'Select Warehouse' : 'Select Vehicle'} *
              </Label>
              {destinations.length === 0 ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  No {destinationType === 'warehouse' ? 'warehouses' : 'vehicles'} available. Please create one in Settings first.
                </div>
              ) : (
                <Select value={destinationId} onValueChange={setDestinationId}>
                  <SelectTrigger className="h-12" data-testid="destination-select">
                    <SelectValue placeholder={`Select ${destinationType}...`} />
                  </SelectTrigger>
                  <SelectContent>
                    {destinations.map((dest) => (
                      <SelectItem key={dest.id} value={dest.id}>
                        <div className="flex items-center gap-2">
                          {destinationType === 'warehouse' ? <Warehouse className="w-4 h-4 text-muted-foreground" /> : <Truck className="w-4 h-4 text-muted-foreground" />}
                          <span>{dest.name}</span>
                          {assignmentData?.default_warehouse_id === dest.id && <span className="text-xs text-primary ml-1">(PO Origin)</span>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Phase 3: optional one-step job allocation */}
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-wide">Allocate to Job <span className="font-normal normal-case text-muted-foreground">(optional)</span></Label>
              <JobAllocationPicker value={destinationType === 'warehouse' ? job : null} onChange={setJob}
                disabled={saving} vehicleSelected={destinationType === 'vehicle'} />
              {jobPrefilled && job && job.id === assignmentData?.po_job?.id && destinationType === 'warehouse' && (
                <p className="px-1 text-xs text-muted-foreground" data-testid="po-assign-job-prefill-note">Pre-filled from this PO's job. Change or clear it if needed.</p>
              )}
            </div>

            <div className="rounded-lg bg-muted/60 p-3 text-sm" data-testid="po-assign-summary">
              {formatQty(totalQuantity)} will be added to <span className="font-medium">{getDestinationName() || 'the selected destination'}</span>
              {destinationType === 'warehouse' && job
                ? <> and <span className="font-medium text-primary">allocated to Job {jobLabel(job)}</span>.</>
                : <> and stay <span className="font-medium">unallocated</span> (available for any job).</>}
            </div>

            {isReceive && assignmentData?.has_pending_assignment && (
              <p className="px-1 text-xs text-muted-foreground" data-testid="po-receive-pending-note">Some items received earlier are still waiting to be assigned. Use Assign on the PO for those.</p>
            )}

            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /><span>{error}</span>
              </div>
            )}

            {/* Info about new parts — reassuring, not scary */}
            {assignmentData?.line_items?.some(li => !li.is_linked_to_parts_list) && (
              <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800" data-testid="new-parts-info">
                <Package className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Items marked "New" aren't in your catalogue yet. They'll be automatically added when you assign.</span>
              </div>
            )}
            {isReceive && poId && <PODocumentsPanel poId={poId} source="receive" title="Packing slip & documents" />}
          </div>
        )}
      </AppDrawer>

      {/* Close Confirmation Dialog — renders above the drawer */}
      <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close without assigning?</AlertDialogTitle>
            <AlertDialogDescription>
              You still have unassigned received items for this PO. You can assign them later from the PO details.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleForceClose}>Close Anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
